import api from "../services/api";
import { IServiceResponse } from "../types/api";

// ── Types (the API contract in docs/superpowers/specs/2026-10-16-shop-catalog-design.md, camelCase) ──

// Catalog setup
export type ShopSize = { id: string; label: string; code: string; displayOrder: number };
export type ShopOptionValue = { id: string; label: string; code: string; swatchHex: string | null; displayOrder: number };
export type ShopOptionType = { id: string; name: string; hasSwatch: boolean; displayOrder: number; values: ShopOptionValue[] };
export type ShopCategory = { id: string; name: string; code: string; displayOrder: number; subcategories: ShopCategory[] };
export type ShopSport = {
  id: string; // TournamentTypes.Id
  name: string; // TournamentTypes.TypeName
  slug: string; // name lowercased, non-alphanumerics → "-" ("beach-volleyball")
  code: string;
  tagline: string | null;
  isVisible: boolean;
  displayOrder: number;
};
export type ShopCatalog = { sizes: ShopSize[]; optionTypes: ShopOptionType[]; categories: ShopCategory[]; sports: ShopSport[] };

// Admin catalog: the same, plus how many products use each row. `sports` also lists the app's sports that have no
// shop settings yet (code "", isVisible false, isSetUp false).
export type AdminShopSize = ShopSize & { productCount: number };
export type AdminShopOptionValue = ShopOptionValue & { productCount: number };
export type AdminShopOptionType = Omit<ShopOptionType, "values"> & { values: AdminShopOptionValue[] };
export type AdminShopCategory = Omit<ShopCategory, "subcategories"> & { productCount: number; subcategories: AdminShopCategory[] };
export type AdminShopSport = ShopSport & { productCount: number; isSetUp: boolean };
export type AdminShopCatalog = {
  sizes: AdminShopSize[];
  optionTypes: AdminShopOptionType[];
  categories: AdminShopCategory[];
  sports: AdminShopSport[];
};

export type SaveShopSizeRequest = { label: string; code: string };
// Values left out are deleted (the server refuses one that is in use); values keep the order sent.
export type SaveShopOptionTypeRequest = {
  name: string;
  hasSwatch: boolean;
  values: { id?: string; label: string; code: string; swatchHex?: string | null }[];
};
export type SaveShopCategoryRequest = { name: string; code: string; parentId?: string | null };
export type SaveShopSportRequest = { code: string; tagline?: string | null; isVisible: boolean };

// Products
export type ShopStyle = { id: string; optionValueIds: string[]; label: string; imageUrls: string[] };

export type ShopVariant = {
  // one SKU
  id: string;
  label: string; // computed SKU label ("Black · M" / "One size")
  stock: number | null;
  available: boolean; // isEnabled && (stock === null || stock > 0)
  displayOrder: number;
  // new
  sku: string;
  styleId: string;
  sizeId: string | null;
  price: number; // effective: priceOverride ?? product price
  priceOverride: number | null;
  isEnabled: boolean;
};

export type ShopProduct = {
  // unchanged fields (old app versions read these)
  id: string;
  name: string;
  description: string | null;
  category: string | null; // display name of the category node (subcategory name when set)
  price: number; // base price
  imageUrls: string[]; // first style's photos
  trackStock: boolean;
  isActive: boolean;
  displayOrder: number;
  variants: ShopVariant[]; // public: enabled SKUs only; admin: all
  // new
  code: string;
  categoryId: string | null; // top-level category id
  subcategoryId: string | null;
  sportIds: string[];
  minPrice: number;
  maxPrice: number; // over the SKUs in `variants`
  optionTypes: ShopOptionType[]; // only the types/values this product uses, in catalog order
  sizes: ShopSize[]; // only the sizes this product uses, in catalog order
  styles: ShopStyle[]; // public: styles with ≥ 1 enabled SKU
  createdAt: string;
};

export type SaveShopProductRequest = {
  name: string;
  description?: string | null;
  categoryId: string | null; // category node (subcategory when the category has any); null only for an unfiled old product (refused)
  sportIds: string[];
  price: number;
  code?: string | null; // blank → generated
  trackStock: boolean;
  isActive: boolean;
  optionValueIds: string[]; // empty for a simple product
  sizeIds: string[]; // empty for a simple product / no sizes
  styles: { optionValueIds: string[]; imageUrls: string[] }[]; // photos per style (by value set)
  skus: {
    // per-SKU settings by (value set, size); combinations not listed get defaults
    optionValueIds: string[];
    sizeId: string | null;
    sku?: string | null; // blank → generated
    stock?: number | null;
    expectedStock?: number | null; // same meaning as today
    priceOverride?: number | null;
    isEnabled: boolean;
  }[];
};

// Orders
export type ShopOrderStatus = "Placed" | "ReadyForPickup" | "Collected" | "Cancelled";
export type ShopPaymentMethod = "Balance" | "Fawry" | "InstaPay" | "Cash";
// RefundDue: a paid guest order was cancelled; staff send the money back by hand, then mark it refunded.
export type ShopPaymentStatus = "Unpaid" | "AwaitingReview" | "Paid" | "Refunded" | "RefundDue";

export interface ShopOrderLine {
  productId: string | null;
  variantId: string | null;
  productName: string;
  sizeLabel: string;
  imageUrl: string | null;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  // new: null only on lines placed before SKUs existed
  sku: string | null;
  variantLabel: string; // = sizeLabel ("Black · M")
}

export interface ShopOrder {
  id: string;
  orderNumber: number;
  status: ShopOrderStatus;
  paymentMethod: ShopPaymentMethod;
  paymentStatus: ShopPaymentStatus;
  total: number;
  currency: string;
  instaPayReceiptUrl: string | null;
  notes: string | null;
  cancelReason: string | null;
  createdAt: string;
  paidAt: string | null;
  readyAt: string | null;
  collectedAt: string | null;
  cancelledAt: string | null;
  lines: ShopOrderLine[];
  // Guests order without an account: userId is null and the contact details come from checkout.
  customer: { userId: string | null; name: string; email: string | null; phone: string | null };
  isGuest: boolean;
  accessToken: string | null;
  canCancel: boolean;
  fawryCheckoutUrl: string | null;
  merchantRefNumber: string | null;
}

export interface ShopOrderFilters {
  status?: ShopOrderStatus | "";
  paymentStatus?: ShopPaymentStatus | "";
  search?: string;
}

// ── Catalog setup ──

export const getAdminShopCatalog = (): Promise<IServiceResponse<AdminShopCatalog>> => api.get("Shop/admin/catalog");

export const createShopSize = (data: SaveShopSizeRequest): Promise<IServiceResponse<ShopSize>> =>
  api.post("Shop/admin/sizes", data);
export const updateShopSize = (id: string, data: SaveShopSizeRequest): Promise<IServiceResponse<ShopSize>> =>
  api.put(`Shop/admin/sizes/${id}`, data);
export const deleteShopSize = (id: string): Promise<IServiceResponse<boolean>> => api.delete(`Shop/admin/sizes/${id}`);
export const reorderShopSizes = (ids: string[]): Promise<IServiceResponse<boolean>> =>
  api.put("Shop/admin/sizes/reorder", { ids });

export const createShopOptionType = (data: SaveShopOptionTypeRequest): Promise<IServiceResponse<ShopOptionType>> =>
  api.post("Shop/admin/option-types", data);
export const updateShopOptionType = (id: string, data: SaveShopOptionTypeRequest): Promise<IServiceResponse<ShopOptionType>> =>
  api.put(`Shop/admin/option-types/${id}`, data);
export const deleteShopOptionType = (id: string): Promise<IServiceResponse<boolean>> =>
  api.delete(`Shop/admin/option-types/${id}`);
export const reorderShopOptionTypes = (ids: string[]): Promise<IServiceResponse<boolean>> =>
  api.put("Shop/admin/option-types/reorder", { ids });

export const createShopCategory = (data: SaveShopCategoryRequest): Promise<IServiceResponse<ShopCategory>> =>
  api.post("Shop/admin/categories", data);
export const updateShopCategory = (id: string, data: SaveShopCategoryRequest): Promise<IServiceResponse<ShopCategory>> =>
  api.put(`Shop/admin/categories/${id}`, data);
export const deleteShopCategory = (id: string): Promise<IServiceResponse<boolean>> =>
  api.delete(`Shop/admin/categories/${id}`);
// ids = the siblings of one parent, in their new order
export const reorderShopCategories = (ids: string[]): Promise<IServiceResponse<boolean>> =>
  api.put("Shop/admin/categories/reorder", { ids });

// Creates or updates a sport's shop settings (tournamentTypeId = ShopSport.id)
export const saveShopSport = (tournamentTypeId: string, data: SaveShopSportRequest): Promise<IServiceResponse<ShopSport>> =>
  api.put(`Shop/admin/sports/${tournamentTypeId}`, data);
export const deleteShopSport = (tournamentTypeId: string): Promise<IServiceResponse<boolean>> =>
  api.delete(`Shop/admin/sports/${tournamentTypeId}`);
export const reorderShopSports = (ids: string[]): Promise<IServiceResponse<boolean>> =>
  api.put("Shop/admin/sports/reorder", { ids });

// ── Products ──

export const getAdminShopProducts = (): Promise<IServiceResponse<ShopProduct[]>> => api.get("Shop/admin/products");

export const suggestShopProductCode = (categoryId: string, sportIds: string[]): Promise<IServiceResponse<string>> =>
  api.get("Shop/admin/products/suggest-code", { params: { categoryId, sportIds: sportIds.join(",") } });

export const createShopProduct = (data: SaveShopProductRequest): Promise<IServiceResponse<ShopProduct>> =>
  api.post("Shop/admin/products", data);

export const updateShopProduct = (id: string, data: SaveShopProductRequest): Promise<IServiceResponse<ShopProduct>> =>
  api.put(`Shop/admin/products/${id}`, data);

export const deleteShopProduct = (id: string): Promise<IServiceResponse<boolean>> =>
  api.delete(`Shop/admin/products/${id}`);

export const reorderShopProducts = (ids: string[]): Promise<IServiceResponse<boolean>> =>
  api.put("Shop/admin/products/reorder", { ids });

// ── Orders ──

export const getAdminShopOrders = (filters: ShopOrderFilters = {}): Promise<IServiceResponse<ShopOrder[]>> => {
  const params: Record<string, string> = {};
  if (filters.status) params.status = filters.status;
  if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
  if (filters.search?.trim()) params.search = filters.search.trim();
  return api.get("Shop/admin/orders", { params });
};

export const getShopOrder = (id: string): Promise<IServiceResponse<ShopOrder>> => api.get(`Shop/orders/${id}`);

export const approveShopOrderPayment = (id: string): Promise<IServiceResponse<ShopOrder>> =>
  api.post(`Shop/admin/orders/${id}/approve-payment`);

export const rejectShopOrderPayment = (id: string, reason?: string): Promise<IServiceResponse<ShopOrder>> =>
  api.post(`Shop/admin/orders/${id}/reject-payment`, { reason: reason?.trim() || undefined });

export const markShopOrderReady = (id: string): Promise<IServiceResponse<ShopOrder>> =>
  api.post(`Shop/admin/orders/${id}/ready`);

export const collectShopOrder = (id: string): Promise<IServiceResponse<ShopOrder>> =>
  api.post(`Shop/admin/orders/${id}/collect`);

export const cancelShopOrder = (id: string, reason?: string): Promise<IServiceResponse<ShopOrder>> =>
  api.post(`Shop/admin/orders/${id}/cancel`, { reason: reason?.trim() || undefined });

export const markShopOrderRefunded = (id: string): Promise<IServiceResponse<ShopOrder>> =>
  api.post(`Shop/admin/orders/${id}/mark-refunded`);
